import { useState, useRef, useEffect } from 'react';
import type { CityOverlapGroup, FriendStay, StayPeriod } from '../../utils/overlapGroups';
import type { Flight } from '../../types';
import { getCityName } from '../../utils/airports';
import { getAirline } from '../../utils/airlines';
import { formatDateShort, daysBetween, parseISO, toDateStr } from '../../utils/dates';
import { MapPin, Home, Star, MessageCircle, Crown, ChevronDown, X, Users, Plane, Pencil, Trash2, Share2 } from 'lucide-react';
import { haptic, hapticMedium } from '../../utils/haptics';
import { shareText } from '../../utils/nativeShare';
import { FriendProfileSheet } from '../profile/FriendProfileSheet';
import { ChatView } from '../chat/ChatView';
import { AvatarImg } from '../shared/Avatar';
import { track } from '../../analytics';
import { DOWNLOAD_URL } from '../../constants/links';

interface CityOverlapCardProps {
  group: CityOverlapGroup;
  currentUserName: string;
  currentUserHandle: string;
  currentUserAirline: string;
  currentUserAvatarUrl?: string;
  isHomeBase?: boolean;
  rosterActive?: boolean;
  premiumGated?: boolean;
  onPremiumTap?: () => void;
  /** Render inline below calendar grid — no outer margin, close button in header */
  inline?: boolean;
  onClose?: () => void;
  onPlanMeetup?: () => void;
  /** When set, scroll Gantt to this date instead of today */
  focusDate?: string;
  /** Flight to show inline above Gantt (merged card) */
  flight?: Flight;
  onEditFlight?: (flight: Flight) => void;
  onDeleteFlight?: (flightId: string) => void;
}

/** Convert "14:30" → 14.5/24 = fraction of day */
function timeToFraction(time?: string): number {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return ((h || 0) + (m || 0) / 60) / 24;
}

function formatOverlapDuration(hours: number): string {
  if (hours < 24) return `${hours}h`;
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

const HOME_DAY_WIDTH = 44; // px per day for scrollable home base Gantt
const MAX_VISIBLE_FRIENDS = 5;
const MAX_FIXED_DAYS = 7; // beyond this, switch to scrollable Gantt

export function CityOverlapCard({ group, currentUserName, currentUserHandle, currentUserAirline, currentUserAvatarUrl, isHomeBase: isHomeBaseProp, rosterActive = true, premiumGated = false, onPremiumTap, inline, onClose, onPlanMeetup, focusDate, flight, onEditFlight, onDeleteFlight }: CityOverlapCardProps) {
  const [friendProfileId, setFriendProfileId] = useState<string | null>(null);
  const [chatTarget, setChatTarget] = useState<{ partnerId: string; prefill?: string } | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const gated = !rosterActive || premiumGated;
  const hasGroupMeetup = !isHomeBaseProp && group.groupDates.length > 0;
  const isHomeBase = isHomeBaseProp ?? group.myStays.some((s) => s.reason === 'free_at_base');
  const totalRangeDays = daysBetween(group.rangeStart, group.rangeEnd);

  // overlapGroups.ts already clips rangeStart/rangeEnd to myStay ± 1 day,
  // so no extra buffer needed here. Just use the range directly.
  // If the range exceeds MAX_FIXED_DAYS, switch to scrollable Gantt.
  const displayRangeStart = group.rangeStart;
  const displayTotalDays = totalRangeDays;
  const useScrollableGantt = !isHomeBaseProp && displayTotalDays > MAX_FIXED_DAYS;

  // Deduplicate friends for display (group stays by user)
  const friendsByUser = new Map<string, FriendStay[]>();
  for (const fs of group.friendStays) {
    const existing = friendsByUser.get(fs.user.id);
    if (existing) {
      existing.push(fs);
    } else {
      friendsByUser.set(fs.user.id, [fs]);
    }
  }

  const friendEntries = [...friendsByUser.entries()];

  // At home base: separate actual visitors (on layover) from same-base locals.
  // Use baseAirport match instead of stay.reason — reason is unreliable because
  // flight departure days at home get reason:'layover' even for locals.
  const visitorEntries = isHomeBase
    ? friendEntries.filter(([, stays]) => stays[0].user.baseAirport !== group.city)
    : friendEntries;
  const localEntries = isHomeBase
    ? friendEntries.filter(([, stays]) => stays[0].user.baseAirport === group.city)
    : [];

  // For Gantt display: show all friends — visitors first, then locals (styled differently)
  const ganttEntries = isHomeBase ? [...visitorEntries, ...localEntries] : friendEntries;
  const localUserIds = new Set(localEntries.map(([id]) => id));
  const hasOverflow = isHomeBase && ganttEntries.length > MAX_VISIBLE_FRIENDS;
  const visibleFriends = isHomeBase && !expanded
    ? ganttEntries.slice(0, MAX_VISIBLE_FRIENDS)
    : ganttEntries;
  const hiddenCount = ganttEntries.length - MAX_VISIBLE_FRIENDS;

  const openChat = (userId: string) => {
    const prefill = `Hey! Saw we overlap in ${getCityName(group.city)}. Want to meet up?`;
    setChatTarget({ partnerId: userId, prefill });
  };

  const shareOverlap = async () => {
    haptic();
    const cityName = getCityName(group.city);
    const dateRange = group.rangeStart === group.rangeEnd
      ? formatDateShort(group.rangeStart)
      : `${formatDateShort(group.rangeStart)}-${formatDateShort(group.rangeEnd)}`;
    const text = `Hey! I'm in ${cityName} ${dateRange}. Let's meet up!\n\nAdd me: rostandfound.app/add/${currentUserHandle}\nDownload: ${DOWNLOAD_URL}`;
    const result = await shareText({ text });
    if (result !== 'cancelled') {
      track('overlap_shared', { city: group.city, method: result === 'shared' ? 'native' : 'clipboard' });
    }
  };

  // Use actual myStays — shows gaps when you're away on trips
  const myStays = group.myStays;

  return (
    <div className={`bg-slate-800 p-3 border ${inline ? 'rounded-lg border-coral/30' : 'rounded-xl'} ${hasGroupMeetup ? 'border-coral/40' : isHomeBase ? 'border-slate-600/50' : 'border-slate-700/50'}`}>
      {/* City header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {isHomeBase ? <Home size={14} className="text-slate-400" /> : <MapPin size={14} className="text-accent" />}
          <span className="font-bold text-slate-100 text-sm">
            {getCityName(group.city)}
          </span>
          <span className="text-xs font-mono text-slate-500">{group.city}</span>
          {isHomeBase && (
            <span className="text-[9px] font-bold text-slate-400 bg-slate-700/50 px-1.5 py-0.5 rounded uppercase tracking-wide">Home</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
        <span className={`px-1.5 py-0.5 text-xs font-bold rounded-lg ${
          isHomeBase && visitorEntries.length === 0
            ? 'bg-slate-700/50 text-slate-400'
            : 'bg-coral/15 text-coral'
        }`}>
          {isHomeBase
            ? [
                visitorEntries.length > 0 && `${visitorEntries.length} visitor${visitorEntries.length !== 1 ? 's' : ''}`,
                localEntries.length > 0 && `${localEntries.length} local${localEntries.length !== 1 ? 's' : ''}`,
              ].filter(Boolean).join(' + ')
            : `${formatOverlapDuration(group.totalOverlapHours)} overlap`}
        </span>
        {inline && onClose && (
          <button onClick={onClose} className="p-1 rounded-lg text-slate-500 hover:text-slate-300 active:scale-90 transition-all">
            <X size={14} />
          </button>
        )}
        </div>
      </div>

      {/* Inline flight detail */}
      {flight && (
        <div className="mb-2 relative">
          <InlineFlightDetail flight={flight} />
          <div className="absolute top-1 right-0 flex items-center gap-1">
            {onEditFlight && !confirmDelete && (
              <button
                onClick={() => { haptic(); onEditFlight(flight); }}
                className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 active:scale-90 transition-all"
              >
                <Pencil size={12} />
              </button>
            )}
            {confirmDelete ? (
              <>
                <button onClick={() => setConfirmDelete(false)} className="text-[11px] text-slate-500 px-1.5 py-0.5 hover:text-slate-300 transition-colors">Cancel</button>
                <button
                  onClick={() => { hapticMedium(); onDeleteFlight?.(flight.id); }}
                  className="text-[11px] text-red-400 font-medium px-1.5 py-0.5 active:scale-95 transition-all"
                >Confirm</button>
              </>
            ) : onDeleteFlight && (
              <button
                onClick={() => { haptic(); setConfirmDelete(true); }}
                className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 active:scale-90 transition-all"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
          <div className="border-t border-slate-700/50 mt-2 mb-1" />
        </div>
      )}

      {/* Gantt chart */}
      {isHomeBase || useScrollableGantt ? (
        <ScrollableGantt
          myStays={myStays}
          friendEntries={visibleFriends}
          localUserIds={localUserIds}
          rangeStart={isHomeBase ? group.rangeStart : displayRangeStart}
          totalRangeDays={isHomeBase ? totalRangeDays : displayTotalDays}
          currentUserName={currentUserName}
          currentUserAirline={currentUserAirline}
          currentUserAvatarUrl={currentUserAvatarUrl}
          gated={gated}
          onTapFriend={(id) => { haptic(); setFriendProfileId(id); }}
          onTapMessage={(id) => { haptic(); openChat(id); }}
          focusDate={focusDate}
        />
      ) : (
        <div className="flex flex-col mb-2">
          <DateAxis rangeStartISO={displayRangeStart} totalDays={displayTotalDays} />
          <GanttRow
            name={currentUserName}
            airlineCode={currentUserAirline}
            avatarUrl={currentUserAvatarUrl}
            stays={myStays}
            rangeStart={displayRangeStart}
            totalRangeDays={displayTotalDays}
            showTimes
            isMe
            barIndex={0}
          />
          {visibleFriends.map(([userId, stays], friendIdx) => (
            <GanttRow
              key={userId}
              name={stays[0].user.name}
              airlineCode={stays[0].airlineCode}
              avatarUrl={stays[0].user.avatarUrl}
              stays={stays}
              rangeStart={displayRangeStart}
              totalRangeDays={displayTotalDays}
              showTimes
              barIndex={friendIdx + 1}
              gated={gated}
              userId={userId}
              onTapFriend={(id) => { haptic(); setFriendProfileId(id); }}
              onTapMessage={(id) => { haptic(); openChat(id); }}
            />
          ))}
        </div>
      )}

      {/* Expand / collapse for home base overflow */}
      {hasOverflow && !expanded && (
        <button
          onClick={() => { haptic(); setExpanded(true); }}
          className="w-full flex items-center justify-center gap-1 py-1 text-[11px] text-slate-400 active:text-slate-300 transition-colors"
        >
          <ChevronDown size={12} />
          <span>{hiddenCount} more</span>
        </button>
      )}

      {/* Gated hint */}
      {gated && friendsByUser.size > 0 && (
        <div className="text-xs text-slate-500 mt-1 ml-1 flex items-center gap-1.5">
          <span>{friendsByUser.size} crew member{friendsByUser.size !== 1 ? 's' : ''} here</span>
          {premiumGated && rosterActive ? (
            <button onClick={onPremiumTap} className="inline-flex items-center gap-1 text-amber-400 font-semibold active:opacity-70">
              <Crown size={10} />
              Unlock with Premium
            </button>
          ) : (
            <span>— Add your flights to reveal names</span>
          )}
        </div>
      )}

      {/* Group meetup callout — hidden when gated */}
      {hasGroupMeetup && !gated && (
        <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 bg-coral/10 border border-coral/20 rounded-lg">
          <Star size={11} fill="currentColor" className="text-coral" />
          <span className="text-xs font-semibold text-coral">
            {friendsByUser.size + 1}-way meetup
          </span>
          <span className="text-xs text-red-300/70">
            {group.groupDates.length === 1
              ? formatDateShort(group.groupDates[0])
              : `${formatDateShort(group.groupDates[0])}–${formatDateShort(group.groupDates[group.groupDates.length - 1])}`}
          </span>
        </div>
      )}

      {/* Action buttons — Share + Plan Meetup */}
      {!gated && friendsByUser.size > 0 && (
        <div className="flex gap-2 mt-2">
          <button
            onClick={shareOverlap}
            className="flex-1 py-2 rounded-lg bg-slate-700/50 border border-slate-600/50 text-slate-300 text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] active:bg-slate-700 transition-all"
          >
            <Share2 size={13} />
            Share
          </button>
          {onPlanMeetup && (
            <button
              onClick={() => { haptic(); onPlanMeetup(); }}
              className="flex-1 py-2 rounded-lg bg-coral/15 border border-coral/30 text-coral text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] active:bg-coral/25 transition-all"
            >
              <Users size={13} />
              Plan Meetup
            </button>
          )}
        </div>
      )}

      {friendProfileId && (
        <FriendProfileSheet friendId={friendProfileId} onClose={() => setFriendProfileId(null)} />
      )}
      {chatTarget && (
        <ChatView partnerId={chatTarget.partnerId} prefill={chatTarget.prefill} onClose={() => setChatTarget(null)} />
      )}
    </div>
  );
}

/**
 * Scrollable Gantt for home base cards.
 * Fixed labels on the left, horizontal scroll on the right.
 * Auto-scrolls to today on mount.
 */
function ScrollableGantt({
  myStays, friendEntries, localUserIds, rangeStart, totalRangeDays,
  currentUserName, currentUserAirline, currentUserAvatarUrl,
  gated, onTapFriend, onTapMessage, focusDate,
}: {
  myStays: StayPeriod[];
  friendEntries: [string, FriendStay[]][];
  localUserIds?: Set<string>;
  rangeStart: string;
  totalRangeDays: number;
  currentUserName: string;
  currentUserAirline: string;
  currentUserAvatarUrl?: string;
  gated: boolean;
  onTapFriend: (id: string) => void;
  onTapMessage: (id: string) => void;
  focusDate?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(true);
  const trackWidth = totalRangeDays * HOME_DAY_WIDTH;
  const today = toDateStr(new Date());

  const updateFades = () => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 8);
    setShowRightFade(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  // Auto-scroll to focusDate (or today) — smooth on focusDate changes, instant on mount
  const hasMounted = useRef(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const targetDate = focusDate || today;
    const rangeStartMs = parseISO(rangeStart).getTime();
    const targetMs = parseISO(targetDate).getTime();
    const dayOffset = Math.max(0, (targetMs - rangeStartMs) / 86400000);
    const scrollTo = Math.max(0, dayOffset * HOME_DAY_WIDTH - el.clientWidth / 2 + HOME_DAY_WIDTH / 2);
    if (hasMounted.current) {
      el.scrollTo({ left: scrollTo, behavior: 'smooth' });
    } else {
      el.scrollLeft = scrollTo;
      hasMounted.current = true;
    }
    requestAnimationFrame(updateFades);
  }, [rangeStart, today, focusDate]);

  // Build date numbers (safe across month boundaries)
  const dates: number[] = [];
  const cursor = parseISO(rangeStart);
  for (let i = 0; i < totalRangeDays; i++) {
    dates.push(cursor.getDate());
    cursor.setDate(cursor.getDate() + 1);
  }

  // Today marker position
  const rangeStartMs = parseISO(rangeStart).getTime();
  const todayMs = parseISO(today).getTime();
  const todayOffset = (todayMs - rangeStartMs) / 86400000;
  const todayPx = todayOffset * HOME_DAY_WIDTH + HOME_DAY_WIDTH / 2;

  return (
    <div className="flex mb-2">
      {/* Fixed labels column */}
      <div className="w-[84px] shrink-0">
        {/* Date axis spacer */}
        <div style={{ height: 22 }} />
        {/* My label */}
        <RowLabel
          name={currentUserName}
          airlineCode={currentUserAirline}
          avatarUrl={currentUserAvatarUrl}
          isMe
        />
        {/* Friend labels */}
        {friendEntries.map(([userId, stays]) => (
          <RowLabel
            key={userId}
            name={stays[0].user.name}
            airlineCode={stays[0].airlineCode}
            avatarUrl={stays[0].user.avatarUrl}
            isLocal={localUserIds?.has(userId)}
            gated={gated}
            userId={userId}
            onTapFriend={onTapFriend}
            onTapMessage={onTapMessage}
          />
        ))}
      </div>

      {/* Scrollable tracks with fade hints */}
      <div className="flex-1 min-w-0 relative overflow-hidden">
        {showLeftFade && (
          <div className="absolute left-0 top-0 bottom-0 w-6 bg-gradient-to-r from-slate-800 to-transparent z-10 pointer-events-none" />
        )}
        {showRightFade && (
          <div className="absolute right-0 top-0 bottom-0 w-6 bg-gradient-to-l from-slate-800 to-transparent z-10 pointer-events-none" />
        )}
        <div
          ref={scrollRef}
          className="overflow-x-auto scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onScroll={updateFades}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
        >
        <div style={{ width: trackWidth, position: 'relative' }}>
          {/* Date axis */}
          <div className="flex items-end pb-1 border-b border-slate-700/50" style={{ height: 22 }}>
            {dates.map((d) => (
              <div
                key={d}
                className="text-center shrink-0"
                style={{ width: HOME_DAY_WIDTH }}
              >
                <span className="text-[11px] text-slate-500 font-mono font-medium">{d}</span>
              </div>
            ))}
          </div>

          {/* Today marker */}
          {todayOffset >= 0 && todayOffset < totalRangeDays && (
            <div
              className="absolute top-[22px] bottom-0 w-px bg-accent/30"
              style={{ left: todayPx }}
            />
          )}

          {/* My bar track */}
          <BarTrack
            stays={myStays}
            rangeStart={rangeStart}
            isHomeMe
            barIndex={0}
          />

          {/* Friend bar tracks */}
          {friendEntries.map(([userId, stays], idx) => (
            <BarTrack
              key={userId}
              stays={stays}
              rangeStart={rangeStart}
              isLocal={localUserIds?.has(userId)}
              gated={gated}
              barIndex={idx + 1}
            />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}

/** Name label for scrollable Gantt (used in fixed column) */
function RowLabel({
  name, airlineCode, avatarUrl, isMe, isLocal, gated, userId, onTapFriend, onTapMessage,
}: {
  name: string;
  airlineCode: string;
  avatarUrl?: string;
  isMe?: boolean;
  isLocal?: boolean;
  gated?: boolean;
  userId?: string;
  onTapFriend?: (id: string) => void;
  onTapMessage?: (id: string) => void;
}) {
  const airline = getAirline(airlineCode);
  const effectiveGated = gated && !isMe;
  const isTappable = !isMe && !effectiveGated && userId && onTapFriend;

  const avatar = effectiveGated ? (
    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-slate-600 text-slate-400">?</span>
  ) : (
    <span
      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden"
      style={{ backgroundColor: airline.color, color: airline.textOnColor }}
    >
      <AvatarImg src={avatarUrl} name={name} />
    </span>
  );

  const nameLabel = (
    <span className={`text-[11px] truncate ${isMe ? 'font-semibold text-accent' : isLocal ? 'text-slate-500' : 'text-slate-400'}`}>
      {isMe ? 'You' : effectiveGated ? 'Crew' : name.split(' ')[0]}
    </span>
  );

  return (
    <div className="flex items-center gap-1 pr-1" style={{ height: 30 }}>
      {isTappable ? (
        <button className="flex items-center gap-1 min-w-0 active:opacity-70" onClick={() => onTapFriend(userId)}>
          {avatar}{nameLabel}
          {isLocal && <Home size={9} className="text-slate-500 shrink-0" />}
        </button>
      ) : (
        <div className="flex items-center gap-1">{avatar}{nameLabel}</div>
      )}
      {isTappable && onTapMessage && (
        <button className="shrink-0 active:scale-90 transition-transform" onClick={() => onTapMessage(userId)}>
          <MessageCircle size={11} className="text-accent" />
        </button>
      )}
    </div>
  );
}

/** Bar track row for scrollable Gantt (no label — just the bar) */
function BarTrack({
  stays, rangeStart,
  isHomeMe, isLocal, gated, barIndex = 0,
}: {
  stays: StayPeriod[] | FriendStay[];
  rangeStart: string;
  isHomeMe?: boolean;
  isLocal?: boolean;
  gated?: boolean;
  barIndex?: number;
}) {
  const rangeStartDate = parseISO(rangeStart);

  return (
    <div className="relative bg-slate-700/40 rounded-[5px] overflow-hidden" style={{ height: 26, marginTop: 2, marginBottom: 2 }}>
      {deduplicateStays(stays).map((stay, i) => {
        const startDate = 'startDate' in stay ? stay.startDate : (stay as StayPeriod).startDate;
        const endDate = 'endDate' in stay ? stay.endDate : (stay as StayPeriod).endDate;
        const arrivalTime = stay.arrivalTime;
        const departureTime = stay.departureTime;

        const barStart = parseISO(startDate);
        const barEnd = parseISO(endDate);
        const offsetDays = (barStart.getTime() - rangeStartDate.getTime()) / 86400000;
        const barDays = (barEnd.getTime() - barStart.getTime()) / 86400000 + 1;

        const startFrac = arrivalTime ? timeToFraction(arrivalTime) : 0;
        const endFrac = departureTime ? timeToFraction(departureTime) : 1;

        const leftPx = (offsetDays + startFrac) * HOME_DAY_WIDTH;
        const widthPx = Math.max((barDays - 1 + endFrac - startFrac) * HOME_DAY_WIDTH, 12);

        const canShowTimes = !gated && !isHomeMe && !isLocal && widthPx > 70;

        return (
          <div
            key={i}
            className="absolute top-[1px] bottom-[1px] rounded-[4px] flex items-center justify-between px-1.5 animate-bar-in"
            style={{
              left: leftPx,
              width: widthPx,
              background: isHomeMe || isLocal
                ? 'rgba(100,116,139,0.15)'
                : gated
                  ? '#4b5563'
                  : 'rgba(148,163,184,0.35)',
              border: isHomeMe || isLocal
                ? `1px dashed ${isLocal ? 'rgba(148,163,184,0.25)' : 'rgba(148,163,184,0.3)'}`
                : '1px solid rgba(148,163,184,0.15)',
              opacity: gated ? 0.4 : isLocal ? 0.7 : 1,
              animationDelay: `${barIndex * 60}ms`,
            }}
          >
            {canShowTimes ? (
              <>
                <span className="text-[10px] font-mono text-white/90 whitespace-nowrap leading-none">{arrivalTime || ''}</span>
                <span className="text-[10px] font-mono text-white/90 whitespace-nowrap leading-none">{departureTime || ''}</span>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// --- Standard (non-scrollable) Gantt components for layover cards ---

function DateAxis({
  rangeStartISO,
  totalDays,
}: {
  rangeStartISO: string;
  totalDays: number;
}) {
  const dates: number[] = [];
  const cursor = parseISO(rangeStartISO);
  for (let i = 0; i < totalDays; i++) {
    dates.push(cursor.getDate());
    cursor.setDate(cursor.getDate() + 1);
  }
  const step = Math.max(1, Math.ceil(dates.length / 8));

  return (
    <div className="flex items-end ml-[84px] mr-1 pb-1 border-b border-slate-700/50" style={{ height: 20 }}>
      {dates.map((d, i) => (
        <div key={d} className="text-center" style={{ width: `${100 / totalDays}%` }}>
          {i % step === 0 && (
            <span className="text-[11px] text-slate-500 font-mono font-medium">{d}</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Deduplicate stays by startDate+endDate so overlapping fragments don't stack bars */
function deduplicateStays(stays: (StayPeriod | FriendStay)[]): (StayPeriod | FriendStay)[] {
  const seen = new Set<string>();
  return stays.filter((s) => {
    const key = `${s.startDate}:${s.endDate}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function GanttRow({
  name, airlineCode, avatarUrl, stays, rangeStart, totalRangeDays,
  showTimes, isMe, barIndex = 0, gated, userId, onTapFriend, onTapMessage,
}: {
  name: string;
  airlineCode: string;
  avatarUrl?: string;
  stays: StayPeriod[] | FriendStay[];
  rangeStart: string;
  totalRangeDays: number;
  showTimes?: boolean;
  isMe?: boolean;
  barIndex?: number;
  gated?: boolean;
  userId?: string;
  onTapFriend?: (userId: string) => void;
  onTapMessage?: (userId: string) => void;
}) {
  const airline = getAirline(airlineCode);
  const rangeStartDate = parseISO(rangeStart);
  const effectiveGated = gated && !isMe;
  const isTappable = !isMe && !effectiveGated && userId && onTapFriend;

  const avatar = effectiveGated ? (
    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 bg-slate-600 text-slate-400">?</span>
  ) : (
    <span
      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 overflow-hidden"
      style={{ backgroundColor: airline.color, color: airline.textOnColor }}
    >
      <AvatarImg src={avatarUrl} name={name} />
    </span>
  );

  const nameLabel = (
    <span className={`text-[11px] truncate ${isMe ? 'font-semibold text-accent' : 'text-slate-400'}`}>
      {isMe ? 'You' : effectiveGated ? 'Crew' : name.split(' ')[0]}
    </span>
  );

  return (
    <div className="flex items-center gap-1.5" style={{ height: 30 }}>
      <div className="w-[84px] flex items-center gap-1 shrink-0">
        {isTappable ? (
          <button className="flex items-center gap-1 min-w-0 active:opacity-70 transition-opacity" onClick={() => onTapFriend(userId)}>
            {avatar}{nameLabel}
          </button>
        ) : (
          <div className="flex items-center gap-1">{avatar}{nameLabel}</div>
        )}
        {isTappable && onTapMessage && (
          <button className="shrink-0 active:scale-90 transition-transform" onClick={() => onTapMessage(userId)}>
            <MessageCircle size={11} className="text-accent" />
          </button>
        )}
      </div>

      <div className="flex-1 relative bg-slate-700/40 rounded-[5px] overflow-hidden" style={{ height: 26 }}>
        {deduplicateStays(stays).map((stay, i) => {
          const barStart = parseISO(stay.startDate);
          const barEnd = parseISO(stay.endDate);
          const offsetDays = (barStart.getTime() - rangeStartDate.getTime()) / 86400000;
          const barDays = (barEnd.getTime() - barStart.getTime()) / 86400000 + 1;

          const startFrac = showTimes && stay.arrivalTime ? timeToFraction(stay.arrivalTime) : 0;
          const endFrac = showTimes && stay.departureTime ? timeToFraction(stay.departureTime) : 1;

          const rawLeft = ((offsetDays + startFrac) / totalRangeDays) * 100;
          const rawWidth = ((barDays - 1 + endFrac - startFrac) / totalRangeDays) * 100;

          const extendsLeft = rawLeft < -1;
          const extendsRight = rawLeft + rawWidth > 101;

          const leftPct = Math.max(rawLeft, 0);
          const widthPct = Math.max(Math.min(rawLeft + rawWidth, 100) - leftPct, 3);

          const canShowTimes = showTimes && !effectiveGated && widthPct > 15;

          return (
            <div
              key={i}
              className="absolute top-[1px] bottom-[1px] rounded-[4px] flex items-center justify-between px-1.5 animate-bar-in"
              style={{
                left: `${leftPct}%`,
                width: `${widthPct}%`,
                background: effectiveGated
                  ? '#4b5563'
                  : 'rgba(148,163,184,0.35)',
                border: effectiveGated ? undefined : '1px solid rgba(148,163,184,0.15)',
                opacity: effectiveGated ? 0.4 : 1,
                animationDelay: `${barIndex * 60}ms`,
              }}
            >
              {canShowTimes ? (
                <>
                  <span className="text-[10px] font-mono text-white/90 whitespace-nowrap leading-none">
                    {extendsLeft ? '\u25C0' : (stay.arrivalTime || '')}
                  </span>
                  <span className="text-[10px] font-mono text-white/90 whitespace-nowrap leading-none">
                    {extendsRight ? '\u25B6' : (stay.departureTime || '')}
                  </span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Compact flight detail for inline display inside overlap cards */
function InlineFlightDetail({ flight }: { flight: Flight }) {
  const airline = getAirline(flight.airlineCode);
  const isNextDay = !!(flight.departureTime && flight.arrivalTime &&
    flight.arrivalTime < flight.departureTime);
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: airline.color }}>
        <Plane size={10} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-slate-100 font-mono">{flight.flightNumber}</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: airline.color, color: airline.textOnColor }}>
            {airline.name}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <span className="text-xs text-slate-300 font-mono">{flight.departureAirport}</span>
          {flight.departureTime && <span className="text-[11px] text-slate-400 font-mono">{flight.departureTime}</span>}
          <span className="text-slate-600 mx-0.5">{'\u2192'}</span>
          <span className="text-xs text-slate-300 font-mono">{flight.arrivalAirport}</span>
          {flight.arrivalTime && (
            <span className="text-[11px] text-slate-400 font-mono">
              {flight.arrivalTime}{isNextDay && <sup className="text-[9px] text-slate-500 ml-px">+1</sup>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
